import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, Subscription, forkJoin, fromEvent, interval, merge, of } from 'rxjs';
import { catchError, filter, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { formatterRut } from 'chilean-formatter';
import { AccountService, AlertService } from 'src/app/services/auth';
import { MenuService } from 'src/app/services/menu/menu-service';
import { OfflineStorageService } from 'src/app/services/offline/offline-storage.service';
import { SyncService } from 'src/app/services/offline/sync.service';
import { Order } from 'src/app/shared/models/orders';
import { ReturnReason } from 'src/app/shared/models/returnReason';
import { environment } from 'src/environments/environment';
import { VIAJES_FIRMA_EMAIL_PATTERN } from 'src/app/services/firma-digital/viajes-firma.mapper';
import { firmaSendErrorMessage } from 'src/app/services/firma-digital/viajes-firma-errors';
import { ViajesFirmaTipo } from 'src/core/apis/viajes.firma';
import { HormigonDispatchFacade } from '../hormigon-dispatch.facade';
import { DispatchMutationResult } from '../hormigon-dispatch.types';
import { HormigonFirmaPadModalComponent } from './hormigon-firma-pad-modal.component';
import {
  HormigonFirmaDatosModalComponent,
  HormigonFirmaDatosModalResult,
} from './hormigon-firma-datos-modal.component';
import {
  emptyFirmaDigitalDraft,
  FIRMA_DIGITAL_ADVERTENCIAS,
  FirmaDigitalDraft,
  FirmaDigitalFirmaMode,
  FirmaDigitalLeftTab,
  FirmaDigitalRightTab,
} from './firma-digital-draft';
import {
  QR_CLIENT_POLL_MS,
  QR_CLIENT_WAIT_MS,
  QrClientFirmaStatus,
  buildSeguimientoAnonimoParams,
  buildSeguimientoQrLink,
  clearQrWaitStartedAt,
  deriveQrClientFirmaStatus,
  formatCountdown,
  hasQrOpened,
  isWaitExpired,
  remainingWaitMs,
  resolvePolledQrStatus,
  resolveQrWaitStartedAt,
} from './qr-client-firma-status';

type QrState = 'syncing' | 'ready' | 'offline';

const FIRMA_RIGHT_TABS: FirmaDigitalRightTab[] = [
  'observacion',
  'agua',
  'devolucion',
  'firma',
  'finalizar',
];

const QR_CLIENT_MODAL_DELAY_MS = 500;

type RecepcionField = 'rut' | 'dv' | 'name' | 'email';
type RecepcionErrors = Partial<Record<RecepcionField, string>>;

const RECEPCION_FIELD_LABELS: Record<RecepcionField, string> = {
  rut: 'RUT',
  dv: 'Dígito verificador',
  name: 'Nombre',
  email: 'Correo',
};

const RECEPCION_FIELD_IDS: Record<RecepcionField, string> = {
  rut: 'firmaRut',
  dv: 'firmaDv',
  name: 'firmaNombre',
  email: 'firmaCorreo',
};

@Component({
  selector: 'app-hormigon-firma-digital-page',
  templateUrl: './hormigon-firma-digital-page.component.html',
  styleUrls: ['./hormigon-firma-digital-page.component.css'],
  standalone: false,
})
export class HormigonFirmaDigitalPageComponent implements OnInit, OnDestroy {
  @ViewChild('modalFirmaExito') modalFirmaExito!: TemplateRef<unknown>;
  @ViewChild('modalFinalizarDespacho') modalFinalizarDespacho!: TemplateRef<unknown>;
  @ViewChild('modalQrClienteFirma') modalQrClienteFirma!: TemplateRef<unknown>;

  leftTab: FirmaDigitalLeftTab = 'datos';
  rightTab: FirmaDigitalRightTab = 'observacion';
  firmaMode: FirmaDigitalFirmaMode = 'chooser';
  isPaperMode = false;
  despachoFinalizado = false;

  order: Order | null = null;
  loading = true;
  loadError = '';
  draft: FirmaDigitalDraft = emptyFirmaDigitalDraft();
  saving = false;
  saveHint = '';
  saveError = '';
  recepcionErrors: RecepcionErrors = {};
  private recepcionSubmitted = false;
  recepcionPadOpen = false;
  private extrasSaving = false;
  aguaFirmaOpening = false;
  devolucionFirmaOpening = false;
  recepcionSigned = false;
  private devolucionActiva = false;
  private anotherAdditionInFlight = false;
  private anotherAdditionPending: boolean | null = null;

  obsText = '';
  comentariosFinal = '';
  devolucionM3: number | null = null;
  devolucionReason = '';
  devolucionSector = '';
  returnReasons: ReturnReason[] = [];
  guidePdfSrc: { data: Uint8Array } | null = null;
  guideLoading = false;
  guideError = false;

  papelPhotoBase64: string | null = null;
  papelGuardado = false;

  readonly advertencias = FIRMA_DIGITAL_ADVERTENCIAS;
  readonly showClientQr = environment.seguimientoAnonimoEnabled !== false;

  qrLink = '';
  qrState: QrState = 'syncing';
  qrClientStatus: QrClientFirmaStatus = deriveQrClientFirmaStatus({});
  qrCountdownLabel = formatCountdown(QR_CLIENT_WAIT_MS);
  qrWaitExpired = false;
  qrPollOffline = false;
  qrPollSecondsLeft = Math.ceil(QR_CLIENT_POLL_MS / 1000);
  surveyVisible = false;

  guiaNoFirmada = false;
  firmaExitoTipo: ViajesFirmaTipo | null = null;

  private qrMonitoring = false;
  private qrMonitorGen = 0;
  private qrWaitStartedAt: number | null = null;
  qrStatusPolling = false;
  private qrStatusPollSub: Subscription | null = null;
  private qrPollTickSub: Subscription | null = null;
  private qrPollCycleStartedAt: number | null = null;
  private qrCountdownSub: Subscription | null = null;
  private qrClientDialogRef: MatDialogRef<unknown> | null = null;
  private qrModalOpenTimer: ReturnType<typeof setTimeout> | null = null;
  private ignoreQrDialogClose = false;
  private alive = true;
  private dialogRef: MatDialogRef<unknown> | null = null;
  private readonly destroy$ = new Subject<void>();
  private backPath = '/pedidospendiente';
  private lastLoadedTkt = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly facade: HormigonDispatchFacade,
    private readonly menu: MenuService,
    private readonly dialog: MatDialog,
    private readonly alertService: AlertService,
    private readonly accountService: AccountService,
    private readonly offlineStorage: OfflineStorageService,
    private readonly syncService: SyncService,
  ) {
    this.menu.btnPrevius = true;
  }

  ngOnInit(): void {
    this.backPath = this.router.url.includes('otrosdespachos')
      ? '/otrosdespachos'
      : '/pedidospendiente';
    this.menu.tilteMenu =
      this.backPath === '/otrosdespachos' ? 'Otros despachos' : 'Despacho actual';

    this.menu.return
      .asObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value) this.goBack();
      });

    this.loadReturnReasons();

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const tkt = params.get('tkt');
      this.isPaperMode = params.get('mode') === 'papel';
      if (!tkt) {
        this.loading = false;
        this.loadError = 'Falta el parámetro tkt.';
        return;
      }
      if (tkt !== this.lastLoadedTkt) {
        this.lastLoadedTkt = tkt;
        this.loadOrder(tkt);
      }
      this.applyPasoFromQuery(params.get('paso'));
    });

    this.offlineStorage.online$
      .pipe(takeUntil(this.destroy$))
      .subscribe((online) => {
        if (this.qrClientDialogRef && !online) {
          this.qrPollOffline = true;
        }
      });
  }

  ngOnDestroy(): void {
    this.alive = false;
    this.resetQrClientFirmaUi();
    this.stopQrMonitoring();
    this.dialogRef?.close();
    this.destroy$.next();
    this.destroy$.complete();
  }

  goBack(): void {
    if (!this.isPaperMode && this.hideRightTabs) {
      this.exitClientMode();
      return;
    }
    this.router.navigate([this.backPath]);
  }

  get hideRightTabs(): boolean {
    return (
      this.isPaperMode ||
      this.despachoFinalizado ||
      this.firmaMode === 'digital' ||
      this.firmaMode === 'qr' ||
      this.firmaMode === 'exito'
    );
  }

  setLeftTab(tab: FirmaDigitalLeftTab): void {
    this.leftTab = tab;
    if (tab === 'guia' && !this.guidePdfSrc && !this.guideLoading) {
      void this.loadGuide();
    }
  }

  setRightTab(tab: FirmaDigitalRightTab): void {
    if (this.hideRightTabs) return;
    this.rightTab = tab;
    if (tab === 'firma') {
      this.firmaMode = 'chooser';
    }
    this.syncPasoQuery(tab);
  }

  private parsePaso(raw: string | null): FirmaDigitalRightTab | null {
    if (!raw) return null;
    return (FIRMA_RIGHT_TABS as string[]).includes(raw)
      ? (raw as FirmaDigitalRightTab)
      : null;
  }

  private applyPasoFromQuery(raw: string | null): void {
    if (this.hideRightTabs) return;
    const paso = this.parsePaso(raw);
    if (!paso) {
      this.syncPasoQuery(this.rightTab);
      return;
    }
    this.rightTab = paso;
    if (paso === 'firma') {
      this.firmaMode = 'chooser';
    }
  }

  private syncPasoQuery(tab: FirmaDigitalRightTab): void {
    if (this.route.snapshot.queryParamMap.get('paso') === tab) return;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { paso: tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  get observacionSinCambios(): boolean {
    const nueva = this.obsText.trim();
    if (nueva === '') return true;
    const actual = (this.order?.guidingRemarks ?? '').trim();
    return nueva === actual || nueva === (this.draft.guidingRemarks ?? '').trim();
  }

  goToNextRightTab(): void {
    if (this.rightTab === 'observacion' && !this.observacionSinCambios) {
      this.saveObservacion(true);
      return;
    }
    this.advanceRightTab();
  }

  get devolucionSi(): boolean {
    return this.draft.devolucionSigned || this.devolucionActiva;
  }

  get aguaFirmante(): { nombre: string; rut: string; comentario: string } | null {
    return this.buildFirmante(
      this.order?.waterSignerName,
      this.order?.waterSignerRut,
      this.order?.waterSignerComment
    );
  }

  get devolucionFirmante(): { nombre: string; rut: string; comentario: string } | null {
    return this.buildFirmante(
      this.order?.returnSignerName,
      this.order?.returnSignerRut,
      this.order?.returnSignerComment
    );
  }

  private buildFirmante(
    nombreRaw: string | undefined,
    rutRaw: string | undefined,
    comentarioRaw: string | undefined
  ): { nombre: string; rut: string; comentario: string } | null {
    const nombre = (nombreRaw ?? '').trim();
    const rut = (rutRaw ?? '').trim();
    if (!nombre && !rut) return null;
    return {
      nombre,
      rut: rut ? formatterRut(rut) : '',
      comentario: (comentarioRaw ?? '').trim(),
    };
  }

  startDigital(): void {
    if (this.draft.isSignature) return;
    this.resetQrClientFirmaUi();
    this.stopQrMonitoring();
    this.rightTab = 'firma';
    this.firmaMode = 'digital';
  }

  startRecepcionFirma(): void {
    if (this.isRecepcionFirmaDisabled()) return;
    void this.startRecepcionPadFlow();
  }

  onFirmaDvChange(value: string): void {
    this.draft.firmaDv = (value ?? '').replace(/[^0-9kK]/g, '').slice(0, 1).toUpperCase();
    this.onRecepcionFieldChange();
  }

  onRecepcionFieldChange(): void {
    if (this.recepcionSubmitted) {
      this.applyRecepcionValidation();
    }
  }

  private applyRecepcionValidation(): boolean {
    this.recepcionErrors = this.validateRecepcion();
    const invalid = (Object.keys(this.recepcionErrors) as RecepcionField[]).map(
      (field) => RECEPCION_FIELD_LABELS[field]
    );
    this.saveError = invalid.length
      ? `Revisa los campos marcados: ${invalid.join(', ')}.`
      : '';
    return invalid.length === 0;
  }

  private validateRecepcion(): RecepcionErrors {
    const errors: RecepcionErrors = {};
    const rut = (this.draft.firmaRut ?? '').trim();
    const dv = (this.draft.firmaDv ?? '').trim();
    const email = (this.draft.firmaCorreo ?? '').trim();

    if (!rut) {
      errors.rut = 'Ingresa el RUT del firmante.';
    }
    if (!dv) {
      errors.dv = 'Ingresa el dígito verificador.';
    }
    if (!errors.rut && !errors.dv) {
      const rutLen = rut.length + dv.length;
      if (!(rutLen === 8 || rutLen === 9)) {
        errors.rut = 'Ingresa un RUT válido.';
      }
    }
    if (!(this.draft.firmaNombre ?? '').trim()) {
      errors.name = 'Ingresa el nombre del firmante.';
    }
    if (email && !VIAJES_FIRMA_EMAIL_PATTERN.test(email)) {
      errors.email = 'El correo ingresado no es válido.';
    }
    return errors;
  }

  startQr(): void {
    if (this.draft.isSignature) return;
    if (!this.showClientQr) {
      this.alertService.error('Firma por QR no disponible.');
      return;
    }
    this.resetQrClientFirmaUi();
    this.stopQrMonitoring();
    this.rightTab = 'firma';
    this.firmaMode = 'qr';
    this.buildQrLink();
    this.qrState = this.offlineStorage.isOnline() ? 'syncing' : 'offline';
    void this.startQrMonitoring();
  }

  closeQrClientFirmaModal(): void {
    const signed = this.qrClientStatus.signed;
    this.qrClientDialogRef?.close();
    if (!signed) return;
    this.resetQrClientFirmaUi();
    this.stopQrMonitoring();
    this.firmaMode = 'chooser';
    this.rightTab = 'finalizar';
    this.syncPasoQuery('finalizar');
  }

  regresarDesdeEsperaExpirada(): void {
    this.exitClientMode();
  }

  get qrClientModalText(): string {
    if (this.qrClientStatus.signed) {
      return 'El cliente firmó la guía';
    }
    return 'Cliente se encuentra firmando la guía en su dispositivo';
  }

  get showQrPollHint(): boolean {
    return (
      this.firmaMode === 'qr' &&
      this.qrState === 'ready' &&
      !this.qrClientDialogRef &&
      (this.qrStatusPolling || this.qrModalOpenTimer != null)
    );
  }

  get qrPollHintText(): string {
    if (this.qrPollOffline) {
      return this.qrPollSecondsLeft > 0
        ? `Sin conexión. Se reintentará en ${this.qrPollSecondsLeft} s`
        : 'Sin conexión. Reintentando…';
    }
    return this.qrPollSecondsLeft > 0
      ? `Verificando el estado actual de la firma mediante QR. Próxima en ${this.qrPollSecondsLeft} s`
      : 'Verificando el estado actual de la firma mediante QR';
  }

  goToFinalizarSinFirma(): void {
    if (this.draft.isSignature) return;
    this.resetQrClientFirmaUi();
    this.stopQrMonitoring();
    this.saveError = '';
    this.guiaNoFirmada = !this.draft.isSignature;
    this.firmaMode = 'chooser';
    this.setRightTab('finalizar');
  }

  exitClientMode(): void {
    this.resetQrClientFirmaUi();
    this.stopQrMonitoring();
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
    }
    this.firmaMode = 'chooser';
    this.rightTab = 'firma';
  }

  get firmaExitoTexto(): string {
    if (this.firmaExitoTipo === 'ADICION_AGUA') {
      return 'Firma adición de agua guardada';
    }
    if (this.firmaExitoTipo === 'DEVOLUCION_HORMIGON') {
      return 'Firma devolución guardada';
    }
    return `Guía Nº ${this.folioLabel}`;
  }

  get folioLabel(): string {
    return this.order?.folioNumberGD != null
      ? String(this.order.folioNumberGD)
      : '—';
  }

  displayOrDash(value: string | number | null | undefined): string {
    if (value == null || value === '') return '—';
    return String(value);
  }

  ticketOrdinal(value: number | null | undefined): string {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    const abs = Math.trunc(Math.abs(n));
    const lastTwo = abs % 100;
    const last = abs % 10;
    let suffix = 'mo';
    if (lastTwo !== 11 && lastTwo !== 12) {
      if (last === 1 || last === 3) suffix = 'er';
      else if (last === 2) suffix = 'do';
      else if (last === 4 || last === 5 || last === 6) suffix = 'to';
      else if (last === 8) suffix = 'vo';
      else if (last === 9) suffix = 'no';
    }
    return `${abs}${suffix}`;
  }

  siNo(value: boolean): string {
    return value ? 'SI' : 'NO';
  }

  saveObservacion(advanceOnSuccess = true): void {
    if (!this.order || this.extrasSaving || this.observacionSinCambios) return;
    const remarks = this.obsText.trim();
    this.draft = { ...this.draft, guidingRemarks: remarks };
    const tkt = this.order.tkt.toString();
    const orderNumber = this.order.orderNumber.toString();
    this.persistFields(
      [this.facade.saveGuidingRemarks(tkt, orderNumber, remarks)],
      advanceOnSuccess ? { onSuccess: () => this.advanceRightTab() } : undefined
    );
  }

  private advanceRightTab(): void {
    const order: FirmaDigitalRightTab[] = [
      'observacion',
      'agua',
      'devolucion',
      'firma',
      'finalizar',
    ];
    const index = order.indexOf(this.rightTab);
    if (index < 0 || index >= order.length - 1) return;
    this.setRightTab(order[index + 1]);
  }

  setWaterAddition(value: boolean): void {
    if (this.aguaFirmaOpening || this.draft.aguaSigned) return;
    if (this.draft.waterAddition === value) return;
    this.draft = { ...this.draft, waterAddition: value };
    if (value) {
      void this.startSignerModalFlow('ADICION_AGUA');
    }
  }

  setAnotherAddition(value: boolean): void {
    if (this.draft.anotherAddition === value) return;
    this.draft = { ...this.draft, anotherAddition: value };
    this.queueAnotherAdditionSave(value);
  }

  closeDialog(): void {
    this.dialogRef?.close();
    this.dialogRef = null;
  }

  setDevolucion(value: boolean): void {
    if (this.devolucionFirmaOpening || this.draft.devolucionSigned) return;
    if (this.devolucionSi === value) return;
    this.devolucionActiva = value;
    if (value) {
      void this.startSignerModalFlow('DEVOLUCION_HORMIGON');
    }
  }

  acceptFirmaSuccess(): void {
    const tipo = this.firmaExitoTipo;
    const ref = this.dialogRef;
    this.dialogRef = null;
    const finish = () => {
      this.firmaExitoTipo = null;
      if (!tipo || tipo === 'ADICION_AGUA') return;
      this.firmaMode = 'chooser';
      this.rightTab = 'finalizar';
      this.syncPasoQuery('finalizar');
    };
    if (!ref) {
      finish();
      return;
    }
    ref.afterClosed().pipe(take(1)).subscribe(() => finish());
    ref.close();
  }

  finalizarEntrega(): void {
    this.firmaMode = 'chooser';
    this.rightTab = 'finalizar';
    this.syncPasoQuery('finalizar');
  }

  onSurveySubmitted(): void {
    this.saveHint = 'Encuesta enviada.';
  }

  isRecepcionFirmaDisabled(): boolean {
    return (
      this.saving || this.recepcionPadOpen || this.draft.isSignature || this.recepcionSigned
    );
  }

  savePapel(): void {
    if (!this.papelPhotoBase64 || !this.order || this.saving) return;
    this.saving = true;
    this.saveError = '';
    const user = this.accountService.userValue;
    this.facade
      .savePaperSignature(
        this.order.tkt.toString(),
        this.order.orderNumber.toString(),
        {
          rut: user.RUT_USUARIO,
          name: user.NOMBRE_USUARIO,
          signature: this.papelPhotoBase64,
          email: '',
        }
      )
      .pipe(take(1))
      .subscribe({
        next: (result) => {
          this.saving = false;
          this.draft = {
            ...this.draft,
            isSignature: true,
            papelFileName: 'foto-guia',
          };
          this.papelGuardado = true;
          this.saveHint =
            result.status === 'queued'
              ? 'Foto guardada offline; se enviará al recuperar conexión.'
              : '';
          this.showDatosPanel();
          if (result.status === 'sent') {
            this.refreshOrder(this.order!.tkt.toString(), true);
          }
        },
        error: (err) => {
          this.saving = false;
          this.saveError = err?.message ?? 'No se pudo guardar la guía en papel.';
          this.alertService.error(this.saveError);
        },
      });
  }

  finalizeMock(): void {
    this.dialogRef = this.dialog.open(this.modalFinalizarDespacho, {
      width: 'min(560px, 95vw)',
      maxWidth: '95vw',
      autoFocus: false,
      disableClose: true,
      panelClass: ['firma-exito-dialog', 'firma-exito-dialog--listo'],
      backdropClass: 'firma-exito-backdrop',
    });
  }

  confirmFinalizeDespacho(): void {
    if (this.saving || !this.order) return;
    const tkt = this.order.tkt.toString();
    const orderNumber = this.order.orderNumber.toString();
    const needsUnsignedGuide = this.guiaNoFirmada && !this.draft.isSignature;

    this.saving = true;
    const clearPending$ = this.facade.clearPendingOrder(tkt, orderNumber, {
      commentsFinal: this.comentariosFinal.trim(),
      finFlujo: true,
    });
    const finalize$ = needsUnsignedGuide
      ? this.facade.saveUnsignedGuide(tkt, orderNumber).pipe(switchMap(() => clearPending$))
      : clearPending$;

    finalize$.pipe(take(1)).subscribe({
      next: () => {
        this.saving = false;
        this.closeDialog();
        this.despachoFinalizado = true;
        this.rightTab = 'finalizar';
      },
      error: (err) => {
        this.saving = false;
        this.saveError = err?.message ?? 'No se pudo finalizar el despacho.';
        this.alertService.error(this.saveError);
      },
    });
  }

  continueAfterFinalize(): void {
    this.router.navigate(['/pedidosEntregados']);
  }

  private async startRecepcionPadFlow(): Promise<void> {
    if (!this.order) return;
    this.recepcionSubmitted = true;
    if (!this.applyRecepcionValidation()) {
      const firstInvalid = (Object.keys(RECEPCION_FIELD_IDS) as RecepcionField[]).find(
        (field) => this.recepcionErrors[field]
      );
      if (firstInvalid) {
        setTimeout(() => document.getElementById(RECEPCION_FIELD_IDS[firstInvalid])?.focus());
      }
      return;
    }
    const rut = (this.draft.firmaRut ?? '').trim();
    const dv = (this.draft.firmaDv ?? '').trim().toUpperCase();
    const name = (this.draft.firmaNombre ?? '').trim();
    const email = (this.draft.firmaCorreo ?? '').trim();
    const comentario = (this.draft.firmaComentarios ?? '').trim();
    this.draft = {
      ...this.draft,
      firmaRut: rut,
      firmaDv: dv,
      firmaNombre: name,
      firmaCorreo: email,
      firmaComentarios: comentario,
    };
    this.recepcionPadOpen = true;
    let png: string | null;
    try {
      png = await this.openSignaturePad('');
    } finally {
      this.recepcionPadOpen = false;
    }
    if (!png) return;
    this.submitCanvasFirma({
      tipoFirma: 'RECEPCION',
      rut: `${rut}${dv}`.toUpperCase(),
      name,
      email,
      signature: png,
      comentario,
    });
  }

  private async startSignerModalFlow(tipo: ViajesFirmaTipo): Promise<void> {
    if (tipo === 'ADICION_AGUA') {
      if (this.aguaFirmaOpening || this.draft.aguaSigned) return;
      this.aguaFirmaOpening = true;
    }
    if (tipo === 'DEVOLUCION_HORMIGON') {
      if (this.devolucionFirmaOpening || this.draft.devolucionSigned) return;
      this.devolucionFirmaOpening = true;
    }
    let handedOff = false;
    try {
      if (!this.order) return;
      const datos = await this.openSignerDatosModal(tipo);
      if (!datos) return;
      this.persistSignerDraft(tipo, datos);
      const png = await this.openSignaturePad('');
      if (!png) return;
      handedOff = tipo === 'ADICION_AGUA' || tipo === 'DEVOLUCION_HORMIGON';
      this.submitCanvasFirma({
        tipoFirma: tipo,
        rut: `${datos.rut}${datos.dv}`.toUpperCase(),
        name: datos.name,
        email: datos.email,
        signature: png,
        comentario: datos.comentario,
      });
    } finally {
      if (!handedOff) this.releaseTrackedFirma(tipo, true);
    }
  }

  private releaseTrackedFirma(tipo: ViajesFirmaTipo, revert: boolean): void {
    if (tipo === 'ADICION_AGUA') {
      this.aguaFirmaOpening = false;
      if (revert) this.revertWaterAdditionOnCancel();
    }
    if (tipo === 'DEVOLUCION_HORMIGON') {
      this.devolucionFirmaOpening = false;
      if (revert) this.revertDevolucionOnCancel();
    }
  }

  private revertWaterAdditionOnCancel(): void {
    if (this.draft.aguaSigned) return;
    this.draft = { ...this.draft, waterAddition: false };
  }

  private revertDevolucionOnCancel(): void {
    if (this.draft.devolucionSigned) return;
    this.devolucionActiva = false;
    this.devolucionM3 = null;
    this.draft = { ...this.draft, m3Returned: null };
  }

  private queueAnotherAdditionSave(value: boolean): void {
    if (!this.order) return;
    if (this.anotherAdditionInFlight) {
      this.anotherAdditionPending = value;
      return;
    }
    this.flushAnotherAddition(value);
  }

  private flushAnotherAddition(value: boolean): void {
    if (!this.order) return;
    this.anotherAdditionInFlight = true;
    const tkt = this.order.tkt.toString();
    const orderNumber = this.order.orderNumber.toString();
    this.facade
      .saveAnotherAddition(tkt, orderNumber, value)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.anotherAdditionInFlight = false;
          const pending = this.anotherAdditionPending;
          this.anotherAdditionPending = null;
          if (pending != null && pending !== value) {
            this.flushAnotherAddition(pending);
            return;
          }
          if (result.status !== 'queued') {
            this.refreshOrder(tkt, true);
          }
        },
        error: (err) => {
          this.anotherAdditionInFlight = false;
          const pending = this.anotherAdditionPending;
          this.anotherAdditionPending = null;
          if (pending != null && pending !== value) {
            this.flushAnotherAddition(pending);
            return;
          }
          this.saveError = err?.message ?? 'No se pudo guardar.';
          this.alertService.error(this.saveError);
        },
      });
  }

  private openSignerDatosModal(
    tipo: ViajesFirmaTipo
  ): Promise<HormigonFirmaDatosModalResult | null> {
    const seed =
      tipo === 'RECEPCION'
        ? {
            rut: this.draft.firmaRut,
            dv: this.draft.firmaDv,
            name: this.draft.firmaNombre,
            email: this.draft.firmaCorreo,
            comentario: this.draft.firmaComentarios,
          }
        : tipo === 'ADICION_AGUA'
          ? {
              rut: this.draft.aguaRut,
              dv: this.draft.aguaDv,
              name: this.draft.aguaNombre,
              email: this.draft.aguaCorreo,
              comentario: this.draft.aguaComentarios,
            }
          : {
              rut: this.draft.devolucionRut,
              dv: this.draft.devolucionDv,
              name: this.draft.devolucionNombre,
              email: this.draft.devolucionCorreo,
              comentario: this.draft.devolucionComentarios,
              m3Returned: this.draft.m3Returned,
            };
    const ref = this.dialog.open(HormigonFirmaDatosModalComponent, {
      width: '640px',
      maxWidth: '95vw',
      disableClose: true,
      hasBackdrop: true,
      panelClass: 'firma-modal-dialog',
      backdropClass: 'firma-modal-backdrop',
      data: { tipoFirma: tipo, ...seed },
    });
    return new Promise((resolve) => {
      ref
        .afterClosed()
        .pipe(take(1))
        .subscribe((value: HormigonFirmaDatosModalResult | undefined) =>
          resolve(value ?? null)
        );
    });
  }

  private persistSignerDraft(
    tipo: ViajesFirmaTipo,
    datos: HormigonFirmaDatosModalResult
  ): void {
    if (tipo === 'RECEPCION') {
      this.draft = {
        ...this.draft,
        firmaRut: datos.rut,
        firmaDv: datos.dv,
        firmaNombre: datos.name,
        firmaCorreo: datos.email,
        firmaComentarios: datos.comentario,
      };
      return;
    }
    if (tipo === 'ADICION_AGUA') {
      this.draft = {
        ...this.draft,
        aguaRut: datos.rut,
        aguaDv: datos.dv,
        aguaNombre: datos.name,
        aguaCorreo: datos.email,
        aguaComentarios: datos.comentario,
      };
      return;
    }
    const m3 = datos.m3Returned ?? this.draft.m3Returned;
    this.devolucionM3 = m3 ?? null;
    this.draft = {
      ...this.draft,
      m3Returned: m3 ?? null,
      devolucionRut: datos.rut,
      devolucionDv: datos.dv,
      devolucionNombre: datos.name,
      devolucionCorreo: datos.email,
      devolucionComentarios: datos.comentario,
    };
  }

  private openFirmaExito(tipo: ViajesFirmaTipo): void {
    this.firmaExitoTipo = tipo;
    this.dialogRef?.close();
    this.dialogRef = this.dialog.open(this.modalFirmaExito, {
      width: 'min(560px, 95vw)',
      maxWidth: '95vw',
      autoFocus: false,
      disableClose: true,
      panelClass: ['firma-exito-dialog', 'firma-exito-dialog--listo'],
      backdropClass: 'firma-exito-backdrop',
    });
  }

  private openSignaturePad(initialValue = ''): Promise<string | null> {
    const ref = this.dialog.open(HormigonFirmaPadModalComponent, {
      width: '94vw',
      maxWidth: '960px',
      data: { initialValue },
      disableClose: true,
      hasBackdrop: true,
      panelClass: 'firma-modal-dialog',
      backdropClass: 'firma-modal-backdrop',
    });
    return new Promise((resolve) => {
      ref
        .afterClosed()
        .pipe(take(1))
        .subscribe((value: string | undefined) => resolve(value ?? null));
    });
  }

  private submitCanvasFirma(options: {
    tipoFirma: ViajesFirmaTipo;
    rut: string;
    name: string;
    email: string;
    signature: string;
    comentario?: string;
  }): void {
    if (!this.order || this.saving) {
      this.releaseTrackedFirma(options.tipoFirma, true);
      return;
    }
    this.saving = true;
    this.saveError = '';
    const tkt = this.order.tkt.toString();
    const isRecepcion = options.tipoFirma === 'RECEPCION';

    this.facade
      .saveCanvasSignature(
        tkt,
        this.order.orderNumber.toString(),
        {
          rut: options.rut,
          name: options.name,
          signature: options.signature,
          email: options.email,
          tipoFirma: options.tipoFirma,
          comentario: options.comentario,
        },
        isRecepcion && !!this.order.isSignature
      )
      .pipe(take(1))
      .subscribe({
        next: async (result) => {
          if (!this.alive) return;

          if (result.status === 'sent') {
            this.onTipoFirmaSuccess(options.tipoFirma, false);
            return;
          }

          if (!this.offlineStorage.isOnline()) {
            this.onTipoFirmaSuccess(options.tipoFirma, true);
            return;
          }

          const waitResult = await this.syncService.waitForOpSent(tkt, 'signedCanvas');
          if (!this.alive) return;
          if (waitResult === 'sent') {
            this.onTipoFirmaSuccess(options.tipoFirma, false);
            return;
          }

          this.saving = false;
          this.releaseTrackedFirma(options.tipoFirma, false);
          this.saveError = this.firmaFailureMessage(tkt);
          this.alertService.error(this.saveError);
        },
        error: (err) => {
          this.saving = false;
          this.releaseTrackedFirma(options.tipoFirma, false);
          this.saveError = firmaSendErrorMessage(err);
          this.alertService.error(this.saveError);
        },
      });
  }

  private firmaFailureMessage(tkt: string): string {
    const failure = this.syncService.consumeLastOpFailure(tkt, 'signedCanvas');
    if (failure?.terminal) {
      return failure.message;
    }
    return 'No se pudo enviar la firma. Quedó guardada y se reintentará.';
  }

  private onTipoFirmaSuccess(tipo: ViajesFirmaTipo, offline: boolean): void {
    this.saving = false;
    this.releaseTrackedFirma(tipo, false);
    const queuedHint = 'Firma guardada offline; se enviará al recuperar conexión.';
    if (tipo === 'RECEPCION') {
      this.recepcionSigned = true;
      this.draft = { ...this.draft, isSignature: true };
      this.guiaNoFirmada = false;
      this.saveHint = offline ? queuedHint : '';
      this.firmaMode = 'exito';
    } else if (tipo === 'ADICION_AGUA') {
      this.draft = { ...this.draft, aguaSigned: true };
      if (this.order) this.rememberAguaSigned(this.order.tkt);
      this.saveHint = '';
      if (this.order) {
        const tkt = this.order.tkt.toString();
        const orderNumber = this.order.orderNumber.toString();
        this.persistFields(
          [this.facade.saveWaterAddition(tkt, orderNumber, !!this.draft.waterAddition)],
          { silent: true }
        );
      }
    } else {
      this.draft = {
        ...this.draft,
        devolucionSigned: true,
      };
      this.devolucionActiva = true;
      if (this.order) this.rememberDevolucionSigned(this.order.tkt);
      this.saveHint = '';
      if (this.order) {
        this.persistFields(
          [
            this.facade.saveM3Returned(
              this.order.tkt.toString(),
              this.order.orderNumber.toString(),
              Number(this.draft.m3Returned ?? 0)
            ),
          ],
          { silent: true }
        );
      }
    }
    this.showDatosPanel();
    if (tipo === 'RECEPCION' && this.order) {
      this.refreshOrder(this.order.tkt.toString(), true, true);
    }
    if (tipo === 'RECEPCION') return;
    this.openFirmaExito(tipo);
  }

  private persistFields(
    requests: Observable<DispatchMutationResult>[],
    options?: { silent?: boolean; onSuccess?: () => void }
  ): void {
    if (!this.order || this.extrasSaving) return;
    this.extrasSaving = true;
    this.saveError = '';
    if (!options?.silent) {
      this.saveHint = '';
      this.showDatosPanel();
    }
    forkJoin(requests)
      .pipe(take(1))
      .subscribe({
        next: (results) => {
          this.extrasSaving = false;
          const queued = results.some((r) => r.status === 'queued');
          if (!options?.silent) {
            if (queued) {
              this.saveHint =
                'Guardado offline; se enviará al recuperar conexión.';
            }
            this.showDatosPanel();
          }
          if (!queued) {
            this.refreshOrder(this.order!.tkt.toString(), true);
          }
          options?.onSuccess?.();
        },
        error: (err) => {
          this.extrasSaving = false;
          this.saveError = err?.message ?? 'No se pudo guardar.';
          this.alertService.error(this.saveError);
        },
      });
  }

  /** Focus left "Datos" so the fiche shows the latest draft after each save. */
  private showDatosPanel(): void {
    this.leftTab = 'datos';
  }

  private loadOrder(tkt: string): void {
    this.loading = true;
    this.loadError = '';
    this.papelGuardado = false;
    this.facade
      .getByTicket(tkt, false, true)
      .pipe(
        catchError(() => this.facade.getByTicket(tkt)),
        take(1)
      )
      .subscribe({
        next: (rows) => {
          this.order = rows?.[0] ?? null;
          if (!this.order) {
            this.loadError = 'No se encontró el despacho.';
            this.loading = false;
            return;
          }
          this.seedDraftFromOrder(this.order);
          this.hydrateQrWaitFromOrder();
          this.buildQrLink();
          this.loading = false;
          if (this.leftTab === 'guia') {
            void this.loadGuide();
          }
        },
        error: () => {
          this.loadError = 'No se pudo cargar el despacho.';
          this.loading = false;
        },
      });
  }

  private refreshOrder(
    tkt: string,
    preserveExtras = false,
    keepLocalSignature = false
  ): void {
    // Keep the values already shown in this screen after a successful save;
    // the follow-up GET may not include the newly persisted extras yet.
    const localExtras = preserveExtras
      ? {
          guidingRemarks: this.draft.guidingRemarks,
          waterAddition: this.draft.waterAddition,
          anotherAddition: this.draft.anotherAddition,
          m3Returned: this.draft.m3Returned,
          reasonToReturn: this.draft.reasonToReturn,
          m3ReturnedPallet: this.draft.m3ReturnedPallet,
          isSignature: this.draft.isSignature,
          aguaSigned: this.draft.aguaSigned,
          devolucionSigned: this.draft.devolucionSigned,
        }
      : null;

    this.facade
      .getByTicket(tkt, preserveExtras, preserveExtras)
      .pipe(take(1))
      .subscribe({
        next: (rows) => {
          const next = rows?.[0];
          if (!next) return;
          this.order = next;
          this.seedDraftFromOrder(next);
          if (localExtras) {
            this.draft = {
              ...this.draft,
              guidingRemarks: localExtras.guidingRemarks,
              waterAddition: localExtras.waterAddition,
              anotherAddition: localExtras.anotherAddition,
              m3Returned: localExtras.m3Returned,
              reasonToReturn: localExtras.reasonToReturn,
              m3ReturnedPallet: localExtras.m3ReturnedPallet,
              isSignature: keepLocalSignature
                ? localExtras.isSignature
                : localExtras.isSignature || !!next.isSignature,
              aguaSigned:
                localExtras.aguaSigned ||
                !!next.waterAdditionConfirmed ||
                this.rememberedAguaSigned(next.tkt),
              devolucionSigned:
                localExtras.devolucionSigned ||
                this.hasConfirmedReturn(next.m3Returned) ||
                this.rememberedDevolucionSigned(next.tkt),
            };
            this.obsText = this.draft.guidingRemarks;
            this.devolucionM3 = this.draft.m3Returned;
            this.devolucionReason = this.draft.reasonToReturn;
            this.devolucionSector = this.draft.m3ReturnedPallet;
          }
        },
      });
  }

  private aguaSignedStorageKey(tkt: string | number): string {
    return `hormigon-agua-signed:${tkt}`;
  }

  private rememberAguaSigned(tkt: string | number): void {
    try {
      sessionStorage.setItem(this.aguaSignedStorageKey(tkt), '1');
    } catch {
      /* el viaje confirmado en servidor sigue bloqueando el toggle */
    }
  }

  private rememberedAguaSigned(tkt: string | number): boolean {
    try {
      return sessionStorage.getItem(this.aguaSignedStorageKey(tkt)) === '1';
    } catch {
      return false;
    }
  }

  private devolucionSignedStorageKey(tkt: string | number): string {
    return `hormigon-devolucion-signed:${tkt}`;
  }

  private rememberDevolucionSigned(tkt: string | number): void {
    try {
      sessionStorage.setItem(this.devolucionSignedStorageKey(tkt), '1');
    } catch {
      /* el m3 guardado en el viaje sigue bloqueando el toggle */
    }
  }

  private rememberedDevolucionSigned(tkt: string | number): boolean {
    try {
      return sessionStorage.getItem(this.devolucionSignedStorageKey(tkt)) === '1';
    } catch {
      return false;
    }
  }

  private hasConfirmedReturn(m3: number | string | null | undefined): boolean {
    if (m3 == null || m3 === ('' as unknown)) return false;
    const n = Number(m3);
    return Number.isFinite(n) && n !== 0;
  }

  private seedDraftFromOrder(order: Order): void {
    const prev = this.draft;
    this.devolucionActiva = false;
    const m3Raw = order.m3Returned;
    let m3Returned: number | null = null;
    if (m3Raw != null && m3Raw !== ('' as unknown)) {
      const n = Number(m3Raw);
      m3Returned = Number.isFinite(n) ? n : null;
    }
    this.draft = {
      ...emptyFirmaDigitalDraft(),
      guidingRemarks: order.guidingRemarks || '',
      waterAddition: !!order.waterAddition,
      anotherAddition: !!order.anotherAddition,
      m3Returned,
      reasonToReturn: order.reasonToReturn != null ? String(order.reasonToReturn) : '',
      m3ReturnedPallet: order.pallet != null ? String(order.pallet) : '',
      isSignature: !!order.isSignature,
      firmaRut: prev.firmaRut,
      firmaDv: prev.firmaDv,
      firmaNombre: prev.firmaNombre,
      firmaCorreo: prev.firmaCorreo,
      firmaComentarios: prev.firmaComentarios,
      firmaCanvas: prev.firmaCanvas,
      aguaRut: prev.aguaRut,
      aguaDv: prev.aguaDv,
      aguaNombre: prev.aguaNombre,
      aguaCorreo: prev.aguaCorreo,
      aguaComentarios: prev.aguaComentarios,
      aguaCanvas: prev.aguaCanvas,
      aguaSigned:
        prev.aguaSigned ||
        !!order.waterAdditionConfirmed ||
        this.rememberedAguaSigned(order.tkt),
      devolucionRut: prev.devolucionRut,
      devolucionDv: prev.devolucionDv,
      devolucionNombre: prev.devolucionNombre,
      devolucionCorreo: prev.devolucionCorreo,
      devolucionComentarios: prev.devolucionComentarios,
      devolucionSigned:
        prev.devolucionSigned ||
        this.hasConfirmedReturn(m3Returned) ||
        this.rememberedDevolucionSigned(order.tkt),
      papelFileName: prev.papelFileName,
    };
    this.obsText = this.draft.guidingRemarks;
    this.devolucionM3 = this.draft.m3Returned;
    this.devolucionReason = this.draft.reasonToReturn;
    this.devolucionSector = this.draft.m3ReturnedPallet;
  }

  private loadReturnReasons(): void {
    this.facade
      .getReturnReasons()
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          this.returnReasons = (data ?? []).map((reason) =>
            reason instanceof ReturnReason
              ? reason
              : new ReturnReason(
                  Number((reason as ReturnReason)?.id ?? 0),
                  String((reason as ReturnReason)?.description ?? reason ?? '')
                )
          );
        },
      });
  }

  private buildQrLink(): void {
    if (!this.order || !this.showClientQr) {
      this.qrLink = '';
      return;
    }
    this.qrLink = buildSeguimientoQrLink(
      buildSeguimientoAnonimoParams(this.accountService.userValue, this.order.tkt)
    );
  }

  private async startQrMonitoring(): Promise<void> {
    const gen = ++this.qrMonitorGen;
    this.qrMonitoring = true;
    if (this.offlineStorage.isOnline()) {
      this.syncService.flushNow();
    }
    await this.checkAndUpdateQrState();

    while (
      this.qrMonitoring &&
      this.qrMonitorGen === gen &&
      this.firmaMode === 'qr' &&
      this.qrState !== 'ready'
    ) {
      await new Promise<void>((resolve) => setTimeout(resolve, 300));
      if (!this.qrMonitoring || this.qrMonitorGen !== gen) break;
      if (this.offlineStorage.isOnline() && this.qrState === 'offline') {
        this.syncService.flushNow();
      }
      await this.checkAndUpdateQrState();
    }

    if (
      this.qrMonitoring &&
      this.qrMonitorGen === gen &&
      this.firmaMode === 'qr' &&
      this.qrState === 'ready'
    ) {
      this.startQrStatusPolling();
    }
  }

  private stopQrMonitoring(): void {
    this.qrMonitoring = false;
    this.qrMonitorGen += 1;
    this.stopQrStatusPolling();
  }

  private resetQrClientFirmaUi(clearWait = false): void {
    this.clearQrModalOpenTimer();
    this.stopQrStatusPolling();
    this.stopQrCountdown();
    if (this.qrClientDialogRef) {
      this.ignoreQrDialogClose = true;
      this.qrClientDialogRef.close();
      this.qrClientDialogRef = null;
    } else {
      this.ignoreQrDialogClose = false;
    }
    this.qrPollOffline = false;
    this.stopQrPollTick();
    this.qrPollSecondsLeft = Math.ceil(QR_CLIENT_POLL_MS / 1000);

    if (clearWait) {
      const tkt = this.order?.tkt.toString();
      if (tkt) clearQrWaitStartedAt(tkt);
      this.qrWaitStartedAt = null;
      this.qrWaitExpired = false;
      this.qrClientStatus = deriveQrClientFirmaStatus({});
      this.qrCountdownLabel = formatCountdown(QR_CLIENT_WAIT_MS);
      return;
    }

    if (this.order) {
      this.qrClientStatus = deriveQrClientFirmaStatus(this.order);
      this.hydrateQrWaitFromOrder();
    }
  }

  private hydrateQrWaitFromOrder(): void {
    if (!this.order || !hasQrOpened(this.order.openedQrLink)) {
      return;
    }
    this.qrWaitStartedAt = resolveQrWaitStartedAt({
      tkt: this.order.tkt.toString(),
      openedQrAt: this.order.openedQrAt,
    });
    const now = Date.now();
    this.qrWaitExpired = isWaitExpired(this.qrWaitStartedAt, now);
    this.qrCountdownLabel = formatCountdown(
      remainingWaitMs(this.qrWaitStartedAt, now)
    );
  }

  private startQrStatusPolling(): void {
    if (this.qrStatusPollSub || !this.order) return;
    const tkt = this.order.tkt.toString();
    this.qrStatusPolling = true;

    const visibilityResume$ = fromEvent(document, 'visibilitychange').pipe(
      filter(() => !document.hidden)
    );

    this.qrStatusPollSub = merge(
      interval(QR_CLIENT_POLL_MS).pipe(startWith(0)),
      visibilityResume$
    )
      .pipe(
        takeUntil(this.destroy$),
        filter(() => this.qrStatusPolling),
        filter(() => this.firmaMode === 'qr'),
        filter(() => this.qrState === 'ready'),
        filter(() => !document.hidden),
        switchMap(() => {
          this.restartQrPollCountdown();
          if (!this.offlineStorage.isOnline()) {
            this.qrPollOffline = true;
            return of(null);
          }
          return this.facade.getByTicket(tkt, true, true).pipe(
            take(1),
            catchError(() => {
              this.qrPollOffline = true;
              return of(null);
            })
          );
        })
      )
      .subscribe((rows) => {
        if (!this.qrStatusPolling || this.firmaMode !== 'qr') return;
        if (!rows) {
          this.qrClientStatus = resolvePolledQrStatus(
            this.qrClientStatus,
            null,
            true
          );
          return;
        }
        const next = rows[0];
        if (!next) return;
        this.qrPollOffline = false;
        this.mergeQrFieldsFromOrder(next);
        this.applyQrClientStatus(deriveQrClientFirmaStatus(next));
      });
  }

  private stopQrStatusPolling(): void {
    this.qrStatusPolling = false;
    this.qrStatusPollSub?.unsubscribe();
    this.qrStatusPollSub = null;
    this.stopQrPollTick();
  }

  private restartQrPollCountdown(): void {
    this.qrPollCycleStartedAt = Date.now();
    this.qrPollSecondsLeft = Math.ceil(QR_CLIENT_POLL_MS / 1000);
    if (this.qrPollTickSub) return;
    this.qrPollTickSub = interval(1000)
      .pipe(startWith(0), takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.qrStatusPolling || this.qrPollCycleStartedAt == null) return;
        const remainingMs = QR_CLIENT_POLL_MS - (Date.now() - this.qrPollCycleStartedAt);
        this.qrPollSecondsLeft = Math.max(0, Math.ceil(remainingMs / 1000));
      });
  }

  private stopQrPollTick(): void {
    this.qrPollTickSub?.unsubscribe();
    this.qrPollTickSub = null;
    this.qrPollCycleStartedAt = null;
  }

  private mergeQrFieldsFromOrder(next: Order): void {
    if (!this.order) {
      this.order = next;
      return;
    }
    this.order = {
      ...this.order,
      openedQrLink: next.openedQrLink,
      openedQrAt: next.openedQrAt,
      signedAt: next.signedAt,
      clientSignature: next.clientSignature,
      isSignature: next.isSignature || this.order.isSignature,
    };
  }

  private applyQrClientStatus(next: QrClientFirmaStatus): void {
    this.qrClientStatus = next;

    if (next.phase === 'waiting_open') {
      return;
    }

    const tkt = this.order?.tkt.toString();
    if (next.signed) {
      if (tkt) clearQrWaitStartedAt(tkt);
      this.draft.isSignature = true;
      this.qrWaitExpired = false;
      this.stopQrStatusPolling();
      this.stopQrCountdown();
      this.scheduleQrClientFirmaModal();
      return;
    }

    if (tkt) {
      this.qrWaitStartedAt = resolveQrWaitStartedAt({
        tkt,
        openedQrAt: this.order?.openedQrAt,
      });
    } else if (this.qrWaitStartedAt == null) {
      this.qrWaitStartedAt = Date.now();
    }

    const now = Date.now();
    this.qrWaitExpired = isWaitExpired(this.qrWaitStartedAt, now);
    this.qrCountdownLabel = formatCountdown(
      remainingWaitMs(this.qrWaitStartedAt, now)
    );

    if (!this.qrWaitExpired) {
      this.startQrCountdown();
    } else {
      this.stopQrCountdown();
      this.stopQrStatusPolling();
    }

    this.scheduleQrClientFirmaModal();
  }

  private startQrCountdown(): void {
    this.stopQrCountdown();
    this.qrCountdownSub = interval(1000)
      .pipe(startWith(0), takeUntil(this.destroy$))
      .subscribe(() => {
        const started = this.qrWaitStartedAt ?? Date.now();
        const remaining = remainingWaitMs(started, Date.now());
        this.qrCountdownLabel = formatCountdown(remaining);
        if (
          !this.qrClientStatus.signed &&
          isWaitExpired(started, Date.now())
        ) {
          this.qrWaitExpired = true;
          this.stopQrStatusPolling();
          this.stopQrCountdown();
        }
      });
  }

  private stopQrCountdown(): void {
    this.qrCountdownSub?.unsubscribe();
    this.qrCountdownSub = null;
  }

  private clearQrModalOpenTimer(): void {
    if (this.qrModalOpenTimer != null) {
      clearTimeout(this.qrModalOpenTimer);
      this.qrModalOpenTimer = null;
    }
  }

  private scheduleQrClientFirmaModal(): void {
    if (this.qrClientDialogRef) {
      if (this.qrClientStatus.signed) {
        this.qrClientDialogRef.updateSize('min(560px, 95vw)');
      }
      return;
    }
    if (this.qrModalOpenTimer != null) return;
    this.qrModalOpenTimer = setTimeout(() => {
      this.qrModalOpenTimer = null;
      if (this.firmaMode !== 'qr') return;
      this.openQrClientFirmaModal();
      if (this.qrClientStatus.signed) {
        this.qrClientDialogRef?.updateSize('min(560px, 95vw)');
      }
    }, QR_CLIENT_MODAL_DELAY_MS);
  }

  private openQrClientFirmaModal(): void {
    if (this.qrClientDialogRef || !this.modalQrClienteFirma) return;
    this.ignoreQrDialogClose = false;
    const dialogRef = this.dialog.open(this.modalQrClienteFirma, {
      width: this.qrClientStatus.signed ? 'min(560px, 95vw)' : 'min(420px, 95vw)',
      maxWidth: '95vw',
      autoFocus: false,
      disableClose: true,
      panelClass: ['firma-exito-dialog', 'firma-exito-dialog--listo'],
      backdropClass: 'firma-exito-backdrop',
    });
    this.qrClientDialogRef = dialogRef;
    dialogRef.afterClosed().subscribe(() => {
      if (this.qrClientDialogRef === dialogRef) {
        this.qrClientDialogRef = null;
      }
      if (this.ignoreQrDialogClose) {
        this.ignoreQrDialogClose = false;
        return;
      }
      this.stopQrStatusPolling();
      this.stopQrCountdown();
    });
  }

  private async checkAndUpdateQrState(): Promise<void> {
    if (!this.qrMonitoring || !this.order) return;
    if (!this.offlineStorage.isOnline()) {
      this.qrState = 'offline';
      return;
    }

    const tkt = this.order.tkt.toString();
    const queue = await this.offlineStorage.getQueue();
    if (!this.qrMonitoring) return;
    const entry = queue.find((e) => e.tkt === tkt);
    const nonTerminalCount =
      entry?.operations.filter(
        (op) =>
          op.type !== 'signed' &&
          op.type !== 'signedCanvas' &&
          op.type !== 'unsigned'
      ).length ?? 0;
    this.qrState = nonTerminalCount === 0 ? 'ready' : 'syncing';
  }

  private async loadGuide(): Promise<void> {
    const routePdfGuide = this.order?.routePdfGuide;
    this.guidePdfSrc = null;
    this.guideError = false;
    if (!routePdfGuide) return;

    this.guideLoading = true;
    try {
      const response = await fetch(routePdfGuide);
      if (!response.ok) throw new Error(`Guide HTTP ${response.status}`);
      this.guidePdfSrc = { data: new Uint8Array(await response.arrayBuffer()) };
    } catch {
      this.guideError = true;
    } finally {
      this.guideLoading = false;
    }
  }
}
